import { useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, ArrowDownRight, RefreshCw } from "lucide-react";
import { api, money, fullMoney } from "@/lib/api";

const Label = ({ children }) => (
  <p className="font-mono-fin text-[10px] font-semibold uppercase tracking-[0.22em] text-[#9A93AD]">{children}</p>
);

const Field = ({ label, testid, value, onChange }) => (
  <div>
    <Label>{label}</Label>
    <input
      data-testid={testid}
      inputMode="numeric"
      value={value}
      placeholder="No change"
      onChange={(e) => onChange(e.target.value.replace(/[^0-9.]/g, ""))}
      className="mt-2 w-full bg-transparent border-b border-[#262336] focus:border-[#E1306C] outline-none font-display text-2xl font-black text-white py-2 transition-colors duration-200 placeholder:text-[#4A4560] placeholder:font-medium placeholder:text-base"
    />
  </div>
);

export const WeeklyUpdateCard = ({ latest, onSaved }) => {
  const [revenue, setRevenue] = useState("");
  const [netIncome, setNetIncome] = useState("");
  const [concentration, setConcentration] = useState("");
  const [recurring, setRecurring] = useState("");
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    const body = {};
    if (revenue) body.annual_revenue = Number(revenue);
    if (netIncome) body.net_income = Number(netIncome);
    if (concentration) body.customer_concentration = Math.min(100, Number(concentration));
    if (recurring) body.recurring_revenue_pct = Math.min(100, Number(recurring));
    if (Object.keys(body).length === 0) return toast.error("Enter at least one updated number");

    setSaving(true);
    try {
      const { data } = await api.post("/valuations/weekly-update", body);
      setResult(data);
      setRevenue(""); setNetIncome(""); setConcentration(""); setRecurring("");
      toast.success(
        data.change_vs_previous === 0
          ? "Valuation unchanged this week"
          : `Valuation ${data.change_vs_previous > 0 ? "up" : "down"} ${money(Math.abs(data.change_vs_previous))}`
      );
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save the update");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="weekly-update-card"
      className="relative bg-[#141220]/90 backdrop-blur-xl border border-[#262336] p-8 rounded-[26px] shadow-[0_12px_44px_rgba(0,0,0,0.55)] md:col-span-4 rise"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Label>Weekly update</Label>
          <p className="mt-3 font-display text-2xl font-bold text-white">Log this week and see the move</p>
          <p className="mt-2 text-sm text-[#9A93AD] max-w-xl leading-relaxed">
            Update only what changed for {latest.business_name}. walker recalculates instantly and tracks
            whether the number went up or down.
          </p>
        </div>
        <button
          onClick={submit}
          disabled={saving}
          data-testid="weekly-update-submit"
          className="grad-btn inline-flex items-center gap-2 px-6 py-3 rounded-full text-white text-sm font-bold disabled:opacity-60"
        >
          <RefreshCw size={16} /> {saving ? "Updating" : "Update valuation"}
        </button>
      </div>

      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
        <Field label="Annual revenue" testid="weekly-revenue" value={revenue} onChange={setRevenue} />
        <Field label="Net income" testid="weekly-net-income" value={netIncome} onChange={setNetIncome} />
        <Field label="Largest customer %" testid="weekly-concentration" value={concentration} onChange={setConcentration} />
        <Field label="Recurring revenue %" testid="weekly-recurring" value={recurring} onChange={setRecurring} />
      </div>

      {result && (
        <div className="mt-10 border-t border-[#262336] pt-8 flex flex-wrap items-end gap-12" data-testid="weekly-update-result">
          <div>
            <Label>New valuation</Label>
            <p className="mt-2 font-display text-3xl font-black grad-text">{fullMoney(result.scenarios.market)}</p>
          </div>
          <div>
            <Label>Move this week</Label>
            <p
              className={`mt-2 inline-flex items-center gap-1 font-mono-fin text-xl font-semibold ${
                result.change_vs_previous >= 0 ? "text-[#3DDC97]" : "text-[#FF6B35]"
              }`}
              data-testid="weekly-update-delta"
            >
              {result.change_vs_previous >= 0 ? <ArrowUpRight size={18} /> : <ArrowDownRight size={18} />}
              {money(Math.abs(result.change_vs_previous))}
            </p>
          </div>
          <div>
            <Label>Adjusted multiple</Label>
            <p className="mt-2 font-mono-fin text-xl font-semibold text-white">{result.adjusted_multiple}x</p>
          </div>
        </div>
      )}
    </div>
  );
};
